import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';
import { useAuthStore } from '@/entities/user';
import { STORE_PATH, storeCustomizePath } from '@/shared/config';
import { buttonVariants, cn } from '@/shared/ui';

export interface GuestQuoteRequestMemberGateProps {
  /** 주소의 상품 id(정규화) — 올바르면 그 상품 에디터로 돌아가는 링크를 둔다. 주소가 잘못됐으면 null. */
  readonly productId: string | null;
  /** 로그인하지 않았을 때 보여 줄 비회원 요청 화면. */
  readonly children: ReactNode;
}

/**
 * 비회원 요청 화면의 회원 차단(설계 G12) — 로그인한 회원은 공개 비회원 접수로 요청하지 않는다(회원 흐름 불변). 로그인 상태면 비회원
 * 폼 대신 에디터의 [견적 요청하기](저장한 디자인으로 회원 견적 요청)로 돌아가라고 안내한다. 에디터는 이 상품의 임시 작업(색상 포함)을
 * 다시 연다. 로그인하지 않았으면 children 을 그대로 보여 준다.
 */
export const GuestQuoteRequestMemberGate = ({
  productId,
  children,
}: GuestQuoteRequestMemberGateProps): ReactElement => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) return <>{children}</>;

  return (
    <div className="flex flex-col items-center gap-4 border-b border-foreground/10 py-16 text-center">
      <p className="text-lg font-bold tracking-[-0.02em]">
        로그인 상태에서는 에디터에서 [견적 요청하기]로 회원 견적을 요청해 주세요
      </p>
      <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
        회원 견적은 디자인을 저장한 뒤 요청하고, 내 견적에서 진행 상황을 확인할 수 있습니다.
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        {productId !== null ? (
          <Link
            to={storeCustomizePath(productId)}
            className={cn(buttonVariants({ size: 'lg' }), 'h-11 px-6')}
          >
            에디터로 돌아가기
          </Link>
        ) : null}
        <Link
          to={STORE_PATH}
          className={cn(
            buttonVariants({ variant: 'outline', size: 'lg' }),
            'h-11 border-foreground/20 px-6',
          )}
        >
          스토어로 가기
        </Link>
      </div>
    </div>
  );
};
